/**
 * Learning Loop view (PROD-592 / PROD-649 / Plan v9 §4.2).
 *
 * Plan v9 §4.2 reworks this view to mirror the canonical prototype at
 * https://ai-native-prototype.vercel.app/care-pathway-studio.html so
 * the React build is visually anchored to the source of truth. The
 * new composition has seven blocks:
 *
 *   1. Headline + subtext ("How the System Learns" + "Every clinician
 *      action becomes training signal").
 *   2. SystemLearnsSwimlane - 6-step horizontal swimlane.
 *   3. LiveSignalFeed - reverse-timeline list of recent decisions
 *      with the "Live N events indexed" badge.
 *   4. SuggestionQualityComparison - Day 1 vs Today 2-column table.
 *   5. ClinicIntelligenceProfile - per-tenant Personalization Score +
 *      4 precision metrics.
 *   6. WhatTheAiLearned - 3 clinic-specific learning bullets.
 *   7. AzureStackTable - infrastructure footer.
 *
 * The Sprint 4.2 5-panel illustrative-projection composition (three
 * MetricBlocks + ConfidenceTimeline + per-tenant sparklines) is
 * retired; the snapshot fields it consumed (eventCounts,
 * nbaStateRatio, beforeAfterByNba, overrideRateTrend,
 * confidenceTimelinePerNba) are preserved on `MetricsSnapshot` so the
 * `MetricsClient` interface stays stable, but the view no longer
 * renders them as primary surfaces. They remain available for any
 * follow-up panel that needs them.
 *
 * HC1 two-mode operation: the view consults `useModeStore` at mount
 * + every refresh tick. When mode is `live` the snapshot is fetched
 * via the App Insights client; when the SDK is absent the store
 * falls back to Demo so the dashboard always renders.
 *
 * HC3 citation discipline: the IllustrativeProjectionBanner (PROD-570)
 * stays retired from this view per PROD-649 founder-approval
 * 2026-05-07 - the panels read from a real telemetry source.
 *
 * HC9 telemetry-real: emits `learning_loop_view` on mount + on every
 * refresh tick. The `blocks_rendered` field carries the new total
 * (7) so the dashboard can detect the rework deployment.
 *
 * HC5: zero em / en dashes anywhere. Hyphens only.
 */
import { useCallback, useEffect, useRef } from 'react';
import { SystemLearnsSwimlane } from '../components/learning-loop/SystemLearnsSwimlane';
import { LiveSignalFeed } from '../components/learning-loop/LiveSignalFeed';
import { SuggestionQualityComparison } from '../components/learning-loop/SuggestionQualityComparison';
import { ClinicIntelligenceProfile } from '../components/learning-loop/ClinicIntelligenceProfile';
import { WhatTheAiLearned } from '../components/learning-loop/WhatTheAiLearned';
import { AzureStackTable } from '../components/learning-loop/AzureStackTable';
import { useLearningLoopStore } from '../state/learning-loop.store';
import { useModeStore } from '../state/mode.store';
import { useTenantStore } from '../state/tenant.store';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';

/**
 * Number of major panels the view renders. Surfaced in the
 * `learning_loop_view` telemetry event so the platform can detect
 * when the rework deployment lands. Constant so callers can compare
 * against an exported value rather than hard-coding 7.
 */
export const LEARNING_LOOP_BLOCKS_RENDERED = 7;

export function LearningLoop() {
  const mode = useModeStore((s) => s.mode);
  const tenantId = useTenantStore((s) => s.tenantId);
  const snapshot = useLearningLoopStore((s) => s.snapshot);
  const refreshInterval = useLearningLoopStore((s) => s.refreshInterval);
  const refresh = useLearningLoopStore((s) => s.refresh);
  const setRefreshInterval = useLearningLoopStore((s) => s.setRefreshInterval);
  const refreshError = useLearningLoopStore((s) => s.refreshError);

  const hasLiveData = snapshot?.hasLiveData ?? false;
  const liveSignals = snapshot?.liveSignals ?? [];
  const eventsIndexedCount = snapshot?.eventsIndexedCount ?? 0;
  const suggestionRows = snapshot?.suggestionQualityRows ?? [];
  const profile = snapshot?.clinicIntelligenceByTenant?.[tenantId] ?? null;
  const learningBullets = snapshot?.learningBulletsByTenant?.[tenantId] ?? [];
  const azureStackRows = snapshot?.azureStackRows ?? [];

  const emitView = useCallback(
    (trigger: 'mount' | 'tick' | 'manual') => {
      emitTelemetry('learning_loop_view', {
        mode,
        tenant_id: tenantId,
        refresh_interval_ms: refreshInterval,
        blocks_rendered: LEARNING_LOOP_BLOCKS_RENDERED,
        has_live_data: hasLiveData,
        trigger,
        correlation_id: newCorrelationId(),
        ts: new Date().toISOString(),
      });
    },
    [mode, tenantId, refreshInterval, hasLiveData],
  );

  // Re-create the emit + refresh references on dep change so the
  // interval callback closes over the latest values.
  const emitViewRef = useRef(emitView);
  emitViewRef.current = emitView;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // Mount + tick effect. Fires `learning_loop_view` on mount with
  // trigger 'mount', then on every interval tick with trigger 'tick'.
  useEffect(() => {
    let cancelled = false;
    void refreshRef.current().then(() => {
      if (!cancelled) emitViewRef.current('mount');
    });
    const handle = setInterval(() => {
      void refreshRef.current().then(() => {
        if (!cancelled) emitViewRef.current('tick');
      });
    }, refreshInterval);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [refreshInterval]);

  return (
    <section
      aria-labelledby="view-title-learn"
      data-testid="learning-loop"
      data-mode={mode}
      data-tenant-id={tenantId}
      data-blocks-rendered={LEARNING_LOOP_BLOCKS_RENDERED}
      data-has-live-data={String(hasLiveData)}
      className="flex flex-col gap-gutter-lg px-gutter-lg py-gutter-xl"
    >
      {/* Block 1: Headline + subtext */}
      <header className="flex flex-col gap-gutter-sm">
        <h2
          id="view-title-learn"
          className="text-signal-2xl font-semibold text-text-primary"
        >
          How the System Learns
        </h2>
        <p className="max-w-2xl text-signal-base text-text-secondary">
          Every clinician action becomes training signal. Six-step loop
          from encounter to indexed event to better next suggestion. No
          model retraining required.
        </p>
        <div className="flex flex-wrap items-center gap-gutter-md text-signal-sm text-text-tertiary">
          <label
            htmlFor="learning-loop-refresh-interval"
            className="flex items-center gap-gutter-sm"
          >
            Refresh interval (seconds):
            <input
              id="learning-loop-refresh-interval"
              type="number"
              min={1}
              max={3600}
              defaultValue={Math.round(refreshInterval / 1000)}
              data-testid="learning-loop-refresh-input"
              className="w-20 rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm text-text-primary"
              onBlur={(e) => {
                const v = Number(e.currentTarget.value);
                if (Number.isFinite(v) && v > 0) {
                  setRefreshInterval(Math.round(v * 1000));
                }
              }}
            />
          </label>
          <span data-testid="learning-loop-mode-badge">
            Mode: {mode === 'live' ? 'Live' : 'Demo'}
          </span>
          <span data-testid="learning-loop-fetch-status">
            {snapshot
              ? `Last fetched ${snapshot.fetchedAt}`
              : 'No snapshot yet'}
          </span>
          {refreshError ? (
            <span
              role="alert"
              data-testid="learning-loop-refresh-error"
              className="text-warning-strong-fg"
            >
              {refreshError}
            </span>
          ) : null}
        </div>
      </header>

      {/* Block 2: 6-step swimlane */}
      <SystemLearnsSwimlane />

      {/* Block 3: Live Signal Feed */}
      <LiveSignalFeed
        signals={liveSignals}
        eventsIndexedCount={eventsIndexedCount}
      />

      {/* Block 4: Suggestion Quality Comparison */}
      <SuggestionQualityComparison
        rows={suggestionRows}
        eventsIndexedCount={eventsIndexedCount}
      />

      {/* Block 5: Clinic Intelligence Profile */}
      {profile ? (
        <ClinicIntelligenceProfile profile={profile} />
      ) : (
        <section
          data-testid="clinic-intelligence-profile-empty"
          className="rounded-signal-lg border border-dashed border-surface-border bg-surface-muted px-gutter-lg py-gutter-md text-signal-sm text-text-secondary"
        >
          No Clinic Intelligence Profile available yet for this tenant.
        </section>
      )}

      {/* Block 6: What the AI has learned */}
      <WhatTheAiLearned bullets={learningBullets} />

      {/* Block 7: Azure Stack footer */}
      <AzureStackTable rows={azureStackRows} />
    </section>
  );
}
